import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', order: 0 });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData);
      } else {
        await axios.post(`${API}/categories`, formData);
      }
      setIsDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', order: 0 });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/categories/${id}`);
        fetchCategories();
      } catch (error) {
        alert('Erro ao excluir categoria');
      }
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, order: category.order });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormData({ name: '', order: 0 });
    setIsDialogOpen(true);
  };

  return (
    <AdminLayout title="Categorias">
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/60 font-inter">Gerencie as categorias de produtos</p>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-category-button"
              onClick={openCreateDialog}
              className="bg-[#FF5500] hover:bg-[#CC4400] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-oswald text-2xl">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-white/80">Nome</Label>
                <Input
                  id="name"
                  data-testid="category-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="order" className="text-white/80">Ordem</Label>
                <Input
                  id="order"
                  data-testid="category-order-input"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <Button
                data-testid="submit-category-button"
                type="submit"
                className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white"
              >
                {editingCategory ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(category => (
          <div
            key={category.id}
            data-testid={`category-item-${category.name}`}
            className="bg-[#18181b] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-oswald font-bold text-white">{category.name}</h3>
                <p className="text-white/60 text-sm font-inter">Ordem: {category.order}</p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`edit-category-${category.name}`}
                  onClick={() => openEditDialog(category)}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
                >
                  <Edit className="w-4 h-4 text-white" />
                </button>
                <button
                  data-testid={`delete-category-${category.name}`}
                  onClick={() => handleDelete(category.id)}
                  className="w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-all"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 text-white/40 font-inter">
          Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
        </div>
      )}
    </AdminLayout>
  );
};

export default Categories;